import { useEffect, useRef } from 'react'
import { NavLink } from 'react-router-dom'
import { ChevronRight, LogOut } from 'lucide-react'
import type { AdminUser } from '@manaro/contracts'
import type { NavItem } from '@/shared/config/navigation.ts'
import { useMediaQuery } from '@/shared/hooks/use-media-query.ts'
import { Avatar } from '@/shared/ui/avatar.tsx'

interface MoreSheetProps {
  user: AdminUser
  items: NavItem[]
  onLogout: () => void
  onClose: () => void
}

/** Bottom sheet behind «Ещё»: who is signed in, the sections that did not fit the bar, and sign-out. */
export function MoreSheet({ user, items, onLogout, onClose }: MoreSheetProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const desktop = useMediaQuery('(min-width: 768px)')

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  // Turning a tablet or unfolding a phone brings the sidebar back; a modal sheet left open would lock the page.
  useEffect(() => {
    if (desktop) dialogRef.current?.close()
  }, [desktop])

  const close = () => dialogRef.current?.close()

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      onClick={(event) => event.target === dialogRef.current && close()}
      aria-label="Ещё"
      className="mx-0 mt-auto mb-0 w-full max-w-none rounded-t-[22px] border-0 bg-surface p-0 text-ink shadow-panel backdrop:bg-[rgb(27_27_24/0.35)]"
    >
      <div className="flex flex-col gap-1 px-4 pt-2.5 pb-[calc(16px+env(safe-area-inset-bottom))]">
        <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-line-strong" aria-hidden />

        <div className="flex items-center gap-3 rounded-control px-2 py-2.5">
          <Avatar name={user.name} className="size-10 text-[14px]" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-[15px] font-medium">{user.name}</div>
            <div className="truncate text-[13px] text-ink-muted">
              {user.role} · {user.email}
            </div>
          </div>
        </div>

        {items.length > 0 && (
          <nav aria-label="Другие разделы" className="flex flex-col border-y border-line-soft py-1">
            {items.map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                onClick={close}
                className="flex min-h-[48px] items-center gap-3 rounded-control px-2 text-[15px] active:bg-surface-hover"
              >
                <Icon className="size-5 text-ink-muted" strokeWidth={1.8} aria-hidden />
                <span className="flex-1">{label}</span>
                <ChevronRight className="size-4 text-ink-faint" aria-hidden />
              </NavLink>
            ))}
          </nav>
        )}

        <button
          type="button"
          onClick={() => {
            close()
            onLogout()
          }}
          className="flex min-h-[48px] items-center gap-3 rounded-control px-2 text-[15px] text-warn-ink active:bg-warn-tint"
        >
          <LogOut className="size-5" strokeWidth={1.8} aria-hidden />
          Выйти
        </button>
      </div>
    </dialog>
  )
}
